/* eslint-disable no-debugger */

import { EditorState } from 'draft-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createContainer } from 'unstated-next';
import {
  BuilderDto,
  ModelDto,
  SubdivisionDto,
  PhaseStructureTypeEnum,
  PhaseDto,
  PhaseTypeEnum,
} from '@vatos-pas/common';
import { ParsedPlace } from 'components/Map';
import { INITIAL_JOB_CREATE } from '../constants';
import dataProvider from 'providers/dataProvider';
import {
  findManyBuilders,
  findManyModels,
  findManySubdivisions,
} from 'services/jobs';

const ZIP_CODE_REGEX = new RegExp(/^\d{5}(-\d{4})?$/);

const useJobCreate = () => {
  const currentSubdivisionRequestId = useRef(0);

  const [buildingId, setBuildingId] = useState<string | null>('');
  const [buildingName, setBuildingName] = useState('');
  const [loadingSubdivisions, setLoadingSubdivisions] = useState(false);
  const [toggleBuilding, setToggleBuilding] = useState(true);
  const [options, setOptions] = useState(EditorState.createEmpty());
  const [errors, setErrors] = useState({});
  const [fields, setFields] = useState<any>(INITIAL_JOB_CREATE);
  const [builders, setBuilders] = useState<BuilderDto[] | null>(null);
  const [subdivisions, setSubdivisions] = useState<SubdivisionDto[]>([]);

  const [isDuplicatedJob, setIsDuplicatedJob] = useState(false);

  const [models, setModels] = useState<ModelDto[] | null>(null);
  const [phases, setPhases] = useState<PhaseDto[]>([]);
  const [loadingPhases, setLoadingPhases] = useState(false);

  const builderOptions = useMemo(() => {
    return (
      builders?.map(builder => ({
        id: builder.id,
        name: builder.name,
      })) ?? []
    );
  }, [builders]);

  const modelOptions = useMemo(() => {
    return (
      models?.map(builder => ({
        ...builder,
        id: builder.id,
        name: `${builder.name} (${builder.externalId})`,
      })) ?? []
    );
  }, [models]);

  const validateFields = () => {
    const newErrors = {};

    // Model Filled
    if (!fields.model) {
      newErrors['model'] = 'The Model is Required';
    }
    // Community Phase
    if (!fields.communityPhase) {
      newErrors['communityPhase'] = 'The Community Phase is Required';
    }

    if (!fields.address1) {
      newErrors['address1'] = 'The Address is Required';
    }
    if (!fields.city) {
      newErrors['city'] = 'The City is Required';
    }
    if (!fields.state) {
      newErrors['state'] = 'The State is Required';
    }
    if (!fields.zip) {
      newErrors['zip'] = 'The Zip Code is Required';
    } else {
      if (!fields.zip.match(ZIP_CODE_REGEX)) {
        newErrors['zip'] = [
          'Zip code must be entered either in 5 digit format (12345) or 9 digit format (12345-1234)',
        ];
      }
    }
    if (!fields.idNumber) {
      newErrors['idNumber'] = 'The Job ID Code is Required';
    }

    // Total Square Footage validation - Required only for DrywallAndPaint workflow
    const selectedSubdivision = subdivisions?.find(
      sub => sub.id === fields.subdivision,
    );
    const isDrywallAndPaint =
      selectedSubdivision?.phaseStructureType ===
      PhaseStructureTypeEnum.DrywallAndPaint;

    if (isDrywallAndPaint) {
      if (
        fields.totalSquareFootage === undefined ||
        fields.totalSquareFootage === null ||
        fields.totalSquareFootage === ''
      ) {
        newErrors['totalSquareFootage'] = 'Total Square Footage is required.';
      } else if (fields.totalSquareFootage <= 0) {
        newErrors['totalSquareFootage'] =
          'Total Square Footage must not be less than 1.';
      }
    }

    // Interior Options Cost validation - Required when checkbox is checked for DrywallAndPaint
    if (isDrywallAndPaint && fields.hasInteriorOptions) {
      if (
        fields.interiorOptionCost === undefined ||
        fields.interiorOptionCost === null
      ) {
        newErrors['interiorOptionCost'] = 'Interior Option Cost is required.';
      } else if (fields.interiorOptionCost < 0) {
        newErrors['interiorOptionCost'] =
          'Interior Option Cost cannot be less than 0.';
      }
    }

    // Exterior Options Cost validation - Required when checkbox is checked for DrywallAndPaint
    if (isDrywallAndPaint && fields.hasExteriorOptions) {
      if (
        fields.exteriorOptionCost === undefined ||
        fields.exteriorOptionCost === null
      ) {
        newErrors['exteriorOptionCost'] = 'Exterior Option Cost is required.';
      } else if (fields.exteriorOptionCost < 0) {
        newErrors['exteriorOptionCost'] =
          'Exterior Option Cost cannot be less than 0.';
      }
    }

    if (isDrywallAndPaint && fields.hasTrimOptions) {
      if (
        fields.trimOptionCost === undefined ||
        fields.trimOptionCost === null ||
        fields.trimOptionCost === ''
      ) {
        newErrors['trimOptionCost'] = 'Trim Option Cost is required.';
      } else if (!(Number(fields.trimOptionCost) > 0)) {
        newErrors['trimOptionCost'] =
          'Trim Option Cost must be greater than 0.';
      }
    }

    if (
      isDrywallAndPaint &&
      fields.hasExteriorPaint &&
      fields.exteriorSidingPaintAmount !== undefined &&
      fields.exteriorSidingPaintAmount !== null &&
      fields.exteriorSidingPaintAmount !== '' &&
      !(Number(fields.exteriorSidingPaintAmount) > 0)
    ) {
      newErrors['exteriorSidingPaintAmount'] =
        'Exterior Siding/Paint must be greater than 0.';
    }

    if (Object.keys(newErrors).length) {
      // Building or BuildingId
      return {
        valid: false,
        errors: newErrors,
      };
    } else {
      return {
        valid: true,
        errors: {},
      };
    }
  };

  const getSupervisors = async (subdivision?: any) => {
    let supervisors = [];
    supervisors = subdivision.subdivisionSupervisors.map(sup => ({
      name: sup.user.firstName + ' ' + sup.user.lastName,
    }));
    return supervisors;
  };

  const getBuildingData = () => {
    const hasBuildingNameOrBuildingId = buildingName || buildingId;

    if (!hasBuildingNameOrBuildingId) return null;

    if (toggleBuilding) {
      return {
        buildingId,
      };
    }

    return {
      building: {
        number: buildingName,
        subdivisionId: fields.subdivision,
      },
    };
  };

  const getBuilders = async () => {
    const builders = await findManyBuilders(dataProvider);

    if (builders) {
      setBuilders(builders);
    }
  };

  const getSubdivisions = async (
    builderId: string | undefined,
    requestId: number,
    name: string,
    subdivisionId: string | undefined | null,
  ) => {
    if (!builderId) return;

    setLoadingSubdivisions(true);

    try {
      const subdivisions = await findManySubdivisions(
        dataProvider,
        {
          builderId,
          active: true,
          ...(subdivisionId && { id: subdivisionId }),
          ...(!subdivisionId && name && { name }),
        },
        25,
      );

      if (subdivisions && requestId === currentSubdivisionRequestId.current) {
        setSubdivisions(subdivisions);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingSubdivisions(false);
    }
  };

  const getModels = async (subdivisionId: string) => {
    const models = await findManyModels(dataProvider, {
      subdivisionId,
      active: true,
    });

    if (models) {
      setModels(models);
    }
    return models;
  };

  // Fetch phases when subdivision changes
  useEffect(() => {
    if (!fields.subdivision) {
      setPhases([]);
      setFields(prev => ({
        ...prev,
        startingPhaseId: undefined,
        startingPhaseApproved: undefined,
        geoDistanceTolerance: undefined,
      }));
      return;
    }
    const selectedSubdivision = subdivisions?.find(
      sub => sub.id === fields.subdivision,
    );
    setFields(prev => ({
      ...prev,
      geoDistanceTolerance: selectedSubdivision?.geoDistanceTolerance ?? undefined,
    }));
    if (!selectedSubdivision?.phaseStructureType) return;

    let cancelled = false;
    const fetchPhases = async () => {
      setLoadingPhases(true);
      try {
        const result = await dataProvider.getList('phase', {
          filter: {
            'phaseStructureType||$eq': selectedSubdivision.phaseStructureType,
          },
          sort: { field: 'priority', order: 'ASC' },
          pagination: { page: 1, perPage: 100 },
        });
        if (cancelled) return;
        const fetchedPhases = result?.data ?? [];
        setPhases(fetchedPhases as PhaseDto[]);

        // Default to Materials phase
        const materialsPhase = fetchedPhases.find(
          (p: any) => p.phaseType === PhaseTypeEnum.Materials,
        );
        if (materialsPhase) {
          setFields(prev => ({
            ...prev,
            startingPhaseId: materialsPhase.id,
            startingPhaseApproved: undefined,
          }));
        }
      } catch (error) {
        console.error('Failed to fetch phases:', error);
      } finally {
        if (!cancelled) setLoadingPhases(false);
      }
    };
    fetchPhases();
    return () => {
      cancelled = true;
    };
  }, [fields.subdivision, subdivisions]);

  const applyParsedPlace = (parsed: ParsedPlace) => {
    setFields(prev => ({
      ...prev,
      address1: parsed.address1,
      address2: parsed.address2,
      city: parsed.city,
      state: parsed.state,
      zip: parsed.zip,
      latitude: Number.isFinite(parsed.lat) ? parsed.lat : null,
      longitude: Number.isFinite(parsed.lng) ? parsed.lng : null,
    }));
  };

  const updateCoordinates = (lat: number, lng: number) => {
    setFields(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
  };

  return {
    fields,
    options,
    applyParsedPlace,
    updateCoordinates,
    buildingId,
    toggleBuilding,
    subdivisions,
    getModels,
    getBuilders,
    builders,
    builderOptions,
    buildingName,
    models,
    modelOptions,
    errors,
    isDuplicatedJob,
    currentSubdivisionRequestId,
    loadingSubdivisions,
    phases,
    loadingPhases,
    setModels,
    setSubdivisions,
    getSubdivisions,
    setIsDuplicatedJob,
    setErrors,
    setBuildingName,
    validateFields,
    getSupervisors,
    getBuildingData,
    setToggleBuilding,
    setBuildingId,
    setOptions,
    setFields,
  };
};

export const JobCreateContainer = createContainer(useJobCreate);
